import React, {useEffect, useState} from 'react';
import {View, Text, TouchableOpacity, Image, Modal, ScrollView, TextInput, ActivityIndicator} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import i18n from 'i18n-js';
import downArrow from '../../../assets/down_arrow.png';
import dropdownStyles from '../../style/dropdownStyles';
import usePersistentMenuState from '../../commons/usePersistentMenuState';
import {
  fetchSavedFilters,
  createSavedFilter,
  updateSavedFilter,
  deleteSavedFilter
} from '../../actions/savedFiltersActions';

const SavedFavouriteViewsDropdown = ({getCurrentFiltersSnapshot, onApplySavedFilter}) => {
  const dispatch = useDispatch();
  const rawFilters =
    useSelector(state => state.savedFilters?.items) || [];
  const savedFilters = Array.isArray(rawFilters) ? rawFilters : [];
  const [isOpen, toggleDropdown] = usePersistentMenuState('savedFavouriteViewsDropdown', false);
  const [selectedViewId, setSelectedViewId] = useState(null);
  const [selectEditOpen, setSelectEditOpen] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [saving, setSaving] = useState(false);
  const [togglingFavouriteId, setTogglingFavouriteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    dispatch(fetchSavedFilters());
  }, [dispatch]);

  const favouriteViews = savedFilters.filter(view => view.isFavourite === true);
  const viewsForButtons = favouriteViews;

  const viewsForDropdown = savedFilters;

  const handleFavouriteButtonPress = (view) => {
    if (!view?.filters) return;
    setSelectedViewId(prev => (prev === view.id ? null : view.id));
    if (typeof onApplySavedFilter === 'function') {
      onApplySavedFilter(view.filters);
    }
  };

  const handleSaveCurrentFilters = () => {
    setNewFilterName('');
    setShowSavePrompt(true);
  };

  const openManageModal = () => {
    setSelectEditOpen(false);
    setShowManageModal(true);
  };

  const handleToggleFavourite = (filter) => {
    if (togglingFavouriteId !== null) return;
    setTogglingFavouriteId(filter.id);
    dispatch(
      updateSavedFilter(
        filter.id,
        { isFavourite: !filter.isFavourite },
        (err) => {
          setTogglingFavouriteId(null);
        }
      )
    );
  };

  const handleDeleteFilter = (filter) => {
    if (deletingId !== null) return;
    setDeletingId(filter.id);
    dispatch(
      deleteSavedFilter(filter.id, () => {
        setDeletingId(null);
      })
    );
  };

  const closeSavePrompt = () => {
    if (saving) return;
    setShowSavePrompt(false);
    setNewFilterName('');
  };

  const handleSubmitSave = (isFavourite = false) => {
    const trimmedName = newFilterName.trim();
    if (!trimmedName) {
      return;
    }
    const filtersSnapshot =
      typeof getCurrentFiltersSnapshot === 'function'
        ? getCurrentFiltersSnapshot()
        : {};

    setSaving(true);
    dispatch(
      createSavedFilter(
        {
          name: trimmedName,
          filters: filtersSnapshot,
          isFavourite
        },
        errorMessage => {
          setSaving(false);
          if (!errorMessage) {
            setShowSavePrompt(false);
            setNewFilterName('');
          }
        }
      )
    );
  };

  return (
    <>
      <View style={[dropdownStyles.dropdownWrapper, dropdownStyles.savedViewsWrapper]}>
        <TouchableOpacity
          style={dropdownStyles.button}
          onPress={toggleDropdown}
          testID="saved_favourite_views_header">
          <View style={dropdownStyles.buttonContent}>
            <Text style={dropdownStyles.text}>{i18n.t('savedFavouriteViews.header')}</Text>
            <Image
              style={[dropdownStyles.icon, isOpen && dropdownStyles.iconOpen]}
              source={downArrow}
            />
          </View>
        </TouchableOpacity>

        {isOpen && (
          <View style={dropdownStyles.savedViewsContent}>
            <View style={dropdownStyles.savedViewsGrid}>
              {viewsForButtons.map((view, index) => (
                <TouchableOpacity
                  key={view.id || index}
                  style={[
                    dropdownStyles.savedViewsViewButton,
                    selectedViewId === view.id && dropdownStyles.dropdownActiveButton
                  ]}
                  onPress={() => handleFavouriteButtonPress(view)}
                  testID={`saved_view_${index}`}>
                  <Text style={dropdownStyles.savedViewsViewButtonText}>{view.name}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <View style={dropdownStyles.savedViewsSelectEditContainer}>
              <TouchableOpacity
                style={dropdownStyles.savedViewsSelectEditRow}
                onPress={() => setSelectEditOpen(prev => !prev)}
                testID="select_edit_saved_views">
                <Text style={dropdownStyles.savedViewsSelectEditText}>{i18n.t('savedFavouriteViews.selectEdit')}</Text>
                <Image
                  style={[dropdownStyles.savedViewsDropdownChevron, selectEditOpen && dropdownStyles.savedViewsDropdownChevronOpen]}
                  source={downArrow}
                />
              </TouchableOpacity>
              {selectEditOpen && (
                <View style={dropdownStyles.savedViewsSelectEditDropdown}>
                  <View style={dropdownStyles.savedViewsSelectEditHeader} testID="saved_views_header_option">
                    <Text style={dropdownStyles.savedViewsSelectEditHeaderText}>{i18n.t('savedFavouriteViews.savedViews')}</Text>
                  </View>
                  <TouchableOpacity
                    style={dropdownStyles.savedViewsSelectEditOptionWithIcon}
                    onPress={openManageModal}
                    testID="manage_saved_views_option">
                    <i className="icon-edit" style={dropdownStyles.savedViewsSelectEditIcon} />
                    <Text style={dropdownStyles.savedViewsSelectEditOptionText}>{i18n.t('savedFavouriteViews.manageSavedViews')}</Text>
                  </TouchableOpacity>
                  {viewsForDropdown.map((view, index) => (
                    <TouchableOpacity
                      key={view.id || index}
                      style={dropdownStyles.savedViewsSelectEditOption}
                      onPress={() => {
                        if (view.filters && typeof onApplySavedFilter === 'function') {
                          onApplySavedFilter(view.filters);
                          setSelectEditOpen(false);
                        }
                      }}
                      testID={`select_edit_option_${index}`}>
                      <Text style={dropdownStyles.savedViewsSelectEditOptionText}>{view.name}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>

            <TouchableOpacity
              style={dropdownStyles.savedViewsSaveButton}
              onPress={handleSaveCurrentFilters}
              testID="save_current_filters">
              <i className="icon-save-icon" style={dropdownStyles.savedViewsSaveIcon} />
              <Text style={dropdownStyles.savedViewsSaveButtonText}>{i18n.t('savedFavouriteViews.saveCurrentFilters')}</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      <Modal
        visible={showSavePrompt}
        transparent
        animationType="fade"
        onRequestClose={() => {
          closeSavePrompt();
        }}>
        <View style={dropdownStyles.savedViewsSavePromptOverlay} testID="save_prompt_overlay">
          <View style={dropdownStyles.savedViewsSaveModalContent}>
            <View style={dropdownStyles.savedViewsManageModalHeader}>
              <Text style={dropdownStyles.savedViewsManageModalTitle}>
                {i18n.t('savedFavouriteViews.savePromptMessage')}
              </Text>
              <TouchableOpacity
                onPress={closeSavePrompt}
                style={dropdownStyles.savedViewsManageModalCloseBtn}
                disabled={saving}
                testID="save_prompt_close">
                <i className="icon-cancel" style={dropdownStyles.savedViewsManageModalCloseIcon} />
              </TouchableOpacity>
            </View>
            <View style={dropdownStyles.savedViewsSaveModalBody}>
              <TextInput
                testID="saved_filter_name_input"
                value={newFilterName}
                onChangeText={setNewFilterName}
                onSubmitEditing={() => handleSubmitSave()}
                placeholder={i18n.t('savedFavouriteViews.savePromptPlaceholder')}
                placeholderTextColor="#849DB8"
                style={dropdownStyles.savedViewsSaveModalInput}
                editable={!saving}
                autoFocus
              />
              {saving && (
                <ActivityIndicator
                  testID="save_prompt_loader"
                  size="small"
                  color="#E7ECF3"
                />
              )}
              <View style={dropdownStyles.savedViewsSaveModalActions}>
                <TouchableOpacity
                  testID="save_prompt_cancel"
                  onPress={closeSavePrompt}
                  disabled={saving}
                  style={[
                    dropdownStyles.savedViewsSaveModalActionButton,
                    dropdownStyles.savedViewsSaveModalSecondaryButton
                  ]}>
                  <Text style={dropdownStyles.savedViewsSaveModalActionText}>
                    {i18n.t('savedFavouriteViews.cancel')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="save_prompt_submit"
                  onPress={() => handleSubmitSave(false)}
                  disabled={saving || !newFilterName.trim()}
                  style={dropdownStyles.savedViewsSaveModalActionButton}>
                  <Text style={dropdownStyles.savedViewsSaveModalActionText}>
                    {i18n.t('savedFavouriteViews.save')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="save_prompt_submit_favourite"
                  onPress={() => handleSubmitSave(true)}
                  disabled={saving || !newFilterName.trim()}
                  style={[
                    dropdownStyles.savedViewsSaveModalActionButton,
                    dropdownStyles.savedViewsSaveModalFavouriteButton
                  ]}>
                  <Text style={dropdownStyles.savedViewsSaveModalActionText}>
                    {i18n.t('savedFavouriteViews.setAsFavourite')}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showManageModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowManageModal(false)}>
        <View style={dropdownStyles.savedViewsManageModalOverlay}>
          <View style={dropdownStyles.savedViewsManageModalContent}>
            <View style={dropdownStyles.savedViewsManageModalHeader}>
              <Text style={dropdownStyles.savedViewsManageModalTitle}>{i18n.t('savedFavouriteViews.manageModalTitle')}</Text>
              <TouchableOpacity
                onPress={() => setShowManageModal(false)}
                style={dropdownStyles.savedViewsManageModalCloseBtn}
                testID="manage_modal_close">
                <i className="icon-cancel" style={dropdownStyles.savedViewsManageModalCloseIcon} />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={dropdownStyles.savedViewsManageModalList}
              contentContainerStyle={dropdownStyles.savedViewsManageModalListContent}
              showsVerticalScrollIndicator={true}>
              {savedFilters.map((filter) => (
                <View key={filter.id} style={dropdownStyles.savedViewsManageModalRow}>
                  <Text style={dropdownStyles.savedViewsManageModalRowText} numberOfLines={1}>
                    {filter.name}
                  </Text>
                  <View style={dropdownStyles.savedViewsManageModalRowIcons}>
                    <TouchableOpacity
                      onPress={() => handleDeleteFilter(filter)}
                      style={dropdownStyles.savedViewsManageModalIconBtn}
                      disabled={deletingId === filter.id}
                      testID={`manage_delete_${filter.id}`}>
                      <i className="icon-cancel" style={dropdownStyles.savedViewsManageModalRowIcon} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => handleToggleFavourite(filter)}
                      style={dropdownStyles.savedViewsManageModalIconBtn}
                      disabled={togglingFavouriteId === filter.id}
                      testID={`manage_favourite_${filter.id}`}>
                      <Text style={dropdownStyles.savedViewsManageModalHeartIcon}>
                        {filter.isFavourite ? '♥' : '♡'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  );
};

export default SavedFavouriteViewsDropdown;
